import { utcToLocal12h, getLocalTimezoneAbbr } from './formatters';

const DISPLAY_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PICKER_DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function shiftDayNumber(dayNumber: number, dayOffset: number): number {
  return ((dayNumber + dayOffset) % 7 + 7) % 7;
}

export type Schedule = {
  minute: number;
  hour: number;
  days: string[];
  ampm: 'AM' | 'PM';
};

export function getScheduleDisplay(cronExpression?: string): string | null {
  if (!cronExpression) return null;

  const parts = cronExpression.split(' ');
  if (parts.length < 5) return null;

  const minutePart = parts[0];
  const hourPart = parts[1];
  const daysPart = parts[4];

  if (!daysPart || !hourPart || !minutePart) return null;

  const dayNumbers = daysPart.split(',').map((d) => parseInt(d, 10)).filter((d) => !isNaN(d));

  if (dayNumbers.length === 0) return null;

  const utcHour = parseInt(hourPart, 10);
  const utcMinute = parseInt(minutePart, 10);

  if (isNaN(utcHour) || isNaN(utcMinute)) return null;

  const { hour12, minute: localMinute, ampm, dayOffset } = utcToLocal12h(utcHour, utcMinute);
  const selectedDays = dayNumbers
    .map((dayNumber) => shiftDayNumber(dayNumber, dayOffset))
    .sort((a, b) => a - b)
    .map((dayNumber) => DISPLAY_DAY_NAMES[dayNumber])
    .filter((dayName): dayName is string => Boolean(dayName));

  const dayLabels = selectedDays.join(', ');
  return `Every ${dayLabels} at ${hour12}:${String(localMinute).padStart(2, '0')} ${ampm} ${getLocalTimezoneAbbr()}`;
}

export function getSchedule(cronExpression?: string): Schedule | null {
  if (!cronExpression) return null;

  const parts = cronExpression.split(' ');
  if (parts.length < 5) return null;

  const minutePart = parts[0];
  const hourPart = parts[1];
  const daysPart = parts[4];

  if (!daysPart || !hourPart || !minutePart) return null;

  const dayNumbers = daysPart.split(',').map((d) => parseInt(d, 10)).filter((d) => !isNaN(d));

  if (dayNumbers.length === 0) return null;

  const hour = parseInt(hourPart, 10);
  const minute = parseInt(minutePart, 10);

  if (isNaN(hour) || isNaN(minute)) return null;

  // Cron stores time in UTC — convert to user's local time for the form picker
  const { hour12, minute: localMinute, ampm, dayOffset } = utcToLocal12h(hour, minute);
  const selectedDays = dayNumbers
    .map((dayNumber) => shiftDayNumber(dayNumber, dayOffset))
    .sort((a, b) => a - b)
    .map((dayNumber) => PICKER_DAY_NAMES[dayNumber])
    .filter((dayName): dayName is string => Boolean(dayName));

  return {
    minute: localMinute,
    hour: hour12,
    days: selectedDays,
    ampm
  };
}
